/**
 * Utility functions for broker connection guard logic
 * Pure functions that can be easily tested and reused
 */

/**
 * Check if user has bypass flags set
 * @param {Object} sessionStorage - sessionStorage object
 * @returns {boolean} - true if bypass is allowed
 */
export const checkBypassFlags = (sessionStorage) => {
  const allowDashboardNoBroker = sessionStorage.getItem('allowDashboardNoBroker');
  const allowAccountNoBroker = sessionStorage.getItem('allowAccountNoBroker');
  return allowDashboardNoBroker === '1' || allowAccountNoBroker === '1';
};

/**
 * Clear bypass flags from sessionStorage
 * @param {Object} sessionStorage - sessionStorage object
 */
export const clearBypassFlags = (sessionStorage) => {
  sessionStorage.removeItem('allowDashboardNoBroker');
  sessionStorage.removeItem('allowAccountNoBroker');
};

/**
 * Evaluate broker connection status
 * @param {Object} connectionStatus - connection status object
 * @returns {Object} - evaluated status
 */
export const evaluateConnectionStatus = (connectionStatus) => {
  const { hasConnections, hasExpiredConnections, hasAnyConnections } = connectionStatus;
  
  return {
    hasActiveConnections: hasConnections,
    hasExpiredConnections: hasExpiredConnections,
    hasAnyConnections: hasAnyConnections,
    shouldShowExpiredUI: hasExpiredConnections && !hasConnections,
    shouldRedirect: !hasAnyConnections && !hasConnections,
    shouldAllowAccess: hasConnections || hasExpiredConnections
  };
};

/**
 * Determine if user should be allowed access
 * @param {Object} user - user object
 * @param {Object} connectionStatus - connection status
 * @param {boolean} loading - loading state
 * @param {boolean} brokerLoading - broker loading state
 * @returns {Object} - access decision
 */
export const determineAccess = (user, connectionStatus, loading, brokerLoading) => {
  // If still loading, wait
  if (loading || brokerLoading) {
    return { allowAccess: false, reason: 'loading' };
  }

  // If no user, redirect to login
  if (!user) {
    return { allowAccess: false, reason: 'no_user', redirectPath: '/login' };
  }

  const status = evaluateConnectionStatus(connectionStatus);

  // If user has active connections, allow access
  if (status.hasActiveConnections) {
    return { allowAccess: true, reason: 'has_active_connections' };
  }

  // If user has expired connections, allow access to show expired UI
  if (status.shouldShowExpiredUI) {
    return { allowAccess: true, reason: 'has_expired_connections' };
  }

  // If no connections at all, should redirect
  if (status.shouldRedirect) {
    return { allowAccess: false, reason: 'no_connections', redirectPath: '/broker-connect' };
  }

  // Default: allow access
  return { allowAccess: true, reason: 'default' };
};

/**
 * Get redirect path with current path preservation
 * @param {string} currentPath - current pathname
 * @param {string} redirectPath - target redirect path
 * @returns {Object} - redirect configuration
 */
export const getRedirectConfig = (currentPath, redirectPath) => {
  const fullPath = currentPath + window.location.search + window.location.hash;
  
  return {
    path: redirectPath,
    replace: true,
    state: redirectPath === '/broker-connect' ? { 
      postConnectRedirect: fullPath 
    } : undefined
  };
};

/**
 * Log broker guard decisions for debugging
 * @param {string} context - context name
 * @param {Object} data - data to log
 */
export const logBrokerGuardDecision = (context, data) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(`🔍 BrokerGuard.${context}:`, data);
  }
};
